import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  HouseLineIcon,
  PlusIcon,
  ShieldCheckIcon,
  ChatCircleTextIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import LinkedSection from "../../components/crm/LinkedSection";
import InlineEditableField from "../../components/crm/InlineEditableField";
import CopyButton from "../../components/CopyButton";
import {
  ConfirmDelete,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import EnrollmentForm from "../../components/billing/EnrollmentForm";
import SubscriptionStatusBadge from "../../components/billing/SubscriptionStatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const REGION_OPTIONS = [
  { value: "st_george", label: "St. George" },
  { value: "las_vegas", label: "Las Vegas" },
];

function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [doc, setDoc] = useState(null);
  const [subs, setSubs] = useState([]);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [propOpen, setPropOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/customers/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    // Subscriptions for this customer (office sees full, techs see slim)
    try {
      const { data } = await api.get("/subscriptions/", {
        params: { customer_id: id },
      });
      setSubs(data || []);
    } catch {
      setSubs([]);
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/customers/${id}`);
      nav("/customers");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Customer">
        <ErrorBanner message={err} />
      </Layout>
    );

  return (
    <Layout title={doc.name} subtitle="Customer record">
      <EntityHeader
        overline="customer"
        title={doc.name}
        breadcrumb={[
          { label: "Customers", to: "/customers" },
          { label: doc.name },
        ]}
        meta={
          <>
            <span className="inline-flex items-center gap-1.5">
              id · {doc.id.slice(0, 8)}
              <CopyButton
                value={doc.id}
                label="Customer ID"
                testId="customer-copy-id"
              />
            </span>
            <span>
              created · {new Date(doc.created_at).toLocaleDateString()}
            </span>
          </>
        }
        canEdit={canWrite}
        canDelete={canWrite}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <InlineEditableField
          label="Email"
          value={doc.email}
          name="email"
          mono
          canEdit={canWrite}
          endpoint={`/customers/${id}`}
          type="email"
          placeholder="Add email"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="customer-inline"
        />
        <InlineEditableField
          label="Phone"
          value={doc.phone}
          name="phone"
          mono
          canEdit={canWrite}
          endpoint={`/customers/${id}`}
          type="tel"
          placeholder="Add phone"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="customer-inline"
        />
        <SmsConsentCard
          customer={doc}
          canWrite={canWrite}
          onChange={(updated) => setDoc({ ...doc, ...updated })}
        />
        <FieldCard
          label="Properties"
          value={String(doc.properties.length)}
          mono
        />
        <InlineEditableField
          label="Notes"
          value={doc.notes}
          name="notes"
          as="textarea"
          canEdit={canWrite}
          endpoint={`/customers/${id}`}
          placeholder="Add notes"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="customer-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <LinkedSection
        title="Linked Properties"
        subtitle="addresses owned by this customer"
        count={doc.properties.length}
        canCreate={canWrite}
        onCreate={() => setPropOpen(true)}
        addLabel="Add Property"
        emptyTitle="No properties yet"
        emptyDescription="Add the addresses where this customer's solar systems live."
        data-testid="customer-properties-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Address</th>
              <th className="text-left px-5 py-3 font-bold">ZIP</th>
              <th className="text-left px-5 py-3 font-bold">Region</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {doc.properties.map((p) => (
              <tr
                key={p.id}
                className="hover:bg-slate-50/60 cursor-pointer"
                onClick={() => nav(`/properties/${p.id}`)}
                data-testid={`property-row-${p.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <HouseLineIcon
                    size={14}
                    weight="duotone"
                    className="text-slate-400"
                  />
                  {p.address}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {p.zip_code}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {p.region.replace("_", " ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      {/* Phase 6 — Subscriptions */}
      <LinkedSection
        title="Protection Plan Subscriptions"
        subtitle="recurring service-plan enrollments"
        count={subs.length}
        canCreate={canWrite}
        onCreate={() => setEnrollOpen(true)}
        addLabel="Enroll"
        emptyTitle="Not enrolled in any plan yet"
        emptyDescription="Enroll this customer in a Silver, Gold, or Platinum protection plan to start tracking recurring revenue."
        data-testid="customer-subscriptions-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Plan</th>
              <th className="text-left px-5 py-3 font-bold">Status</th>
              <th className="text-right px-5 py-3 font-bold">Amount</th>
              <th className="text-left px-5 py-3 font-bold">Next renewal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subs.map((s) => (
              <tr
                key={s.id}
                className="hover:bg-slate-50/60 cursor-pointer"
                onClick={() => nav(`/subscriptions/${s.id}`)}
                data-testid={`customer-sub-row-${s.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <ShieldCheckIcon
                    size={14}
                    weight="duotone"
                    className="text-amber-600"
                  />
                  {s.plan_name}
                </td>
                <td className="px-5 py-3">
                  <SubscriptionStatusBadge status={s.status} />
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-900 text-right">
                  {s.amount_cents !== undefined ? fmtCents(s.amount_cents) : "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {s.current_period_end || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      {canWrite && (
        <>
          <EnrollmentForm
            open={enrollOpen}
            onClose={() => setEnrollOpen(false)}
            customerId={id}
            onCreated={(s) => {
              setEnrollOpen(false);
              nav(`/subscriptions/${s.id}`);
            }}
          />
          <EditCustomerModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            customer={doc}
            onSaved={(d) => {
              setEditOpen(false);
              setDoc({ ...doc, ...d });
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Customer"
            busy={delBusy}
          />
          <CreatePropertyModal
            open={propOpen}
            onClose={() => setPropOpen(false)}
            customerId={doc.id}
            onCreated={() => {
              setPropOpen(false);
              load();
            }}
          />
        </>
      )}
    </Layout>
  );
}

export function FieldCard({ label, value, mono = false, className = "" }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-sm p-4 ${className}`}
    >
      <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div
        className={`mt-1.5 text-sm ${mono ? "font-data" : ""} text-slate-900 break-words whitespace-pre-wrap`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function SmsConsentCard({ customer, canWrite, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggled = !!customer.sms_consent;
  const hasPhone = !!customer.phone;
  const disabled = !canWrite || busy || !hasPhone;

  const toggle = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/customers/${customer.id}/sms-consent`,
        { consent: !toggled }
      );
      onChange(data);
      toast.success(
        data.sms_consent ? "SMS consent recorded" : "SMS consent revoked"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update consent");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="customer-sms-consent"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
        <ChatCircleTextIcon size={12} weight="duotone" />
        SMS consent
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          disabled={disabled}
          data-testid="customer-sms-consent-toggle"
          role="switch"
          aria-checked={toggled}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
            toggled ? "bg-emerald-500" : "bg-slate-300"
          } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              toggled ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
        <span
          className={`text-sm font-semibold ${
            toggled ? "text-emerald-700" : "text-slate-600"
          }`}
        >
          {toggled ? "Opted in" : "Not opted in"}
        </span>
      </div>
      {!hasPhone && (
        <div className="mt-2 text-[11px] text-amber-700">
          Add a phone number first.
        </div>
      )}
      {toggled && customer.sms_consent_at && (
        <div className="mt-2 text-[11px] text-slate-500">
          Since {new Date(customer.sms_consent_at).toLocaleDateString()}
        </div>
      )}
      <div className="mt-2 text-[10px] text-slate-400 leading-snug">
        Customer agreed to receive service + billing SMS. Reply STOP to opt out.
      </div>
    </div>
  );
}

function EditCustomerModal({ open, onClose, customer, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: customer.name,
        email: customer.email || "",
        phone: customer.phone || "",
        notes: customer.notes || "",
      });
      setErr("");
    }
  }, [open, customer]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || null,
      };
      const { data } = await api.patch(`/customers/${customer.id}`, payload);
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Customer"
      subtitle="update record"
      data-testid="customer-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" required>
          <TextInput
            required
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            data-testid="customer-edit-name"
          />
        </Field>
        <Field label="Email">
          <TextInput
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            data-testid="customer-edit-email"
          />
        </Field>
        <Field label="Phone">
          <TextInput
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            data-testid="customer-edit-phone"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            data-testid="customer-edit-notes"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="customer-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function CreatePropertyModal({ open, onClose, customerId, onCreated }) {
  const [form, setForm] = useState({
    address: "",
    zip_code: "",
    region: "st_george",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        address: "",
        zip_code: "",
        region: "st_george",
        notes: "",
      });
      setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        address: form.address,
        zip_code: form.zip_code,
        region: form.region,
        notes: form.notes || null,
      };
      const { data } = await api.post("/properties/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Property"
      subtitle="link a new address"
      data-testid="property-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Address" required>
          <TextInput
            required
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
            data-testid="property-address-input"
          />
        </Field>
        <Field label="ZIP" required>
          <TextInput
            required
            value={form.zip_code}
            onChange={(v) => setForm({ ...form, zip_code: v })}
            data-testid="property-zip-input"
          />
        </Field>
        <Field label="Region" required>
          <Select
            value={form.region}
            onChange={(v) => setForm({ ...form, region: v || "st_george" })}
            options={REGION_OPTIONS}
            placeholder=""
            data-testid="property-region-input"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            data-testid="property-notes-input"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="property-submit"
          >
            {busy ? "Creating…" : "Add Property"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
